import * as React from 'react'

import { Alert, Button, type Icon } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import FormRow from 'modules/core/components/FormRow'
import TextInput from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import i18n from 'modules/core/lib/i18n'

import type QuestionCategory from 'modules/surveys/store/models/QuestionCategory'
import type QuestionsTemplateCategory from 'modules/surveys/store/models/QuestionsTemplateCategory'

interface Props {
  category?: QuestionCategory | QuestionsTemplateCategory | null | undefined
  onClose: () => void
  onSubmit: (data: Record<string, unknown>) => Promise<unknown>
  modalIllustration: React.ComponentProps<typeof Icon.Large>['icon']
  modalTitle: string
  modalDescription: string
  modalPlaceholder: string
  modalLabel: string
  modalButton: string
  modalAlert?: string
}

class QuestionCategoryForm extends Form {
  constructor(values: {}) {
    super(values, {
      name: 'string',
    })
  }
}

const MAX_SECTION_NAME_LENGHT = 50

export default function CategoriesForm({
  modalIllustration,
  modalTitle,
  modalDescription,
  modalLabel,
  modalPlaceholder,
  modalButton,
  modalAlert,
  category,
  onSubmit,
  onClose,
}: Props) {
  const form = React.useRef(
    new QuestionCategoryForm({
      question_category_id: category?.attributes.id,
      name: category?.attributes.name,
    })
  ).current

  const getPayload = () => {
    const formData = form.data()

    const payload = {
      name: formData.name,
      question_category_id: category?.attributes.id,
    }

    return payload
  }

  const performAction = async (closePortal: ClosePortalFn) => {
    const payload = getPayload()
    const success = await onSubmit(payload)

    if (success) closePortal()
  }

  const validateNameLenght = (name: string) => {
    return name.length <= MAX_SECTION_NAME_LENGHT
      ? ''
      : i18n.t('surveys.form.question_categories.new.errors.title_too_long', {
          max_characters: MAX_SECTION_NAME_LENGHT,
        })
  }

  return (
    <Modal
      illustration={modalIllustration}
      title={modalTitle}
      description={modalDescription}
      onClose={onClose}
      onSubmit={performAction}
      footer={<Button submit>{modalButton}</Button>}
    >
      <FormRow>
        <TextInput
          label={modalLabel}
          field={form.get('name')}
          placeholder={modalPlaceholder}
          validation={validateNameLenght}
          mandatory
        />
      </FormRow>

      {modalAlert && <Alert color="warning">{modalAlert}</Alert>}
    </Modal>
  )
}
